import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FolderKanban, MessageSquare, Plus, Pencil, Trash2, 
  Eye, Mail, Phone, Clock, CheckCircle, X, Image as ImageIcon, Upload, Loader2, GripVertical,
  Star, ChevronUp, ChevronDown, Video, Play, ArrowUpToLine
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { publicApi, adminApi } from '@/lib/api';
import { refreshSiteContent } from '@/lib/useSiteContent';

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  ongoing: 'bg-amber-100 text-amber-800',
  upcoming: 'bg-blue-100 text-blue-800',
};

const initialProjectForm = {
  name: '',
  location: '',
  description: '',
  project_type: 'residential',
  status: 'ongoing',
  featured: false,
  images: [],
  videos: []
};

export default function AdminDashboard() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [aboutImages, setAboutImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState(initialProjectForm);
  const [imageInput, setImageInput] = useState({ url: '', caption: '' });
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoCaptionInput, setVideoCaptionInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedProjectIndex, setDraggedProjectIndex] = useState(null);
  const [dragOverProjectIndex, setDragOverProjectIndex] = useState(null);
  const [savingProjectOrder, setSavingProjectOrder] = useState(false);
  const [siteContent, setSiteContent] = useState({});
  const [siteContentDraft, setSiteContentDraft] = useState({});
  const [savingContent, setSavingContent] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const slideFileInputRef = useRef(null);
  const aboutFileInputRef = useRef(null);
  const directorFileInputRef = useRef(null);
  
  const activeTab = location.pathname === '/admin/contacts' ? 'contacts' 
                  : location.pathname === '/admin/projects' ? 'projects'
                  : location.pathname === '/admin/slides' ? 'slides'
                  : location.pathname === '/admin/about' ? 'about'
                  : location.pathname === '/admin/content' ? 'content'
                  : 'dashboard';
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, contactsRes, slidesRes, aboutRes, contentRes] = await Promise.all([
        publicApi.getProjects(),
        adminApi.getContacts(),
        adminApi.getHeroSlides(),
        adminApi.getAboutImages(),
        publicApi.getSiteContent(),
      ]);
      setProjects(projectsRes.data);
      setContacts(contactsRes.data);
      setHeroSlides(slidesRes.data);
      setAboutImages(aboutRes.data);
      setSiteContent(contentRes.data || {});
      setSiteContentDraft(contentRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // About Images handlers
  const handleAboutImageUpload = async (e, section = 'main') => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    setUploading(true);

    // For singleton sections (e.g. director), remove existing first
    if (section === 'director') {
      const existing = aboutImages.filter((img) => img.section === 'director');
      for (const img of existing) {
        try { await adminApi.deleteAboutImage(img.id); } catch (_) {}
      }
    }
    
    for (const file of validFiles) {
      try {
        const uploadRes = await adminApi.uploadImage(file);
        const imageData = {
          id: crypto.randomUUID(),
          url: uploadRes.data.url,
          alt: file.name.replace(/\.[^/.]+$/, ''),
          section,
          order: aboutImages.length
        };
        await adminApi.addAboutImage(imageData);
        toast.success(`${file.name} added to About page`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    if (aboutFileInputRef.current) {
      aboutFileInputRef.current.value = '';
    }
    if (directorFileInputRef.current) {
      directorFileInputRef.current.value = '';
    }
    fetchData();
  };
  
  const handleDeleteAboutImage = async (imageId) => {
    if (!window.confirm('Remove this image from the About page?')) return;
    
    try {
      await adminApi.deleteAboutImage(imageId);
      toast.success('Image removed');
      fetchData();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };
  
  // Hero Slides handlers
  const handleSlideUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    setUploading(true);
    
    for (const file of validFiles) {
      try {
        const uploadRes = await adminApi.uploadImage(file);
        const slideData = {
          id: crypto.randomUUID(),
          url: uploadRes.data.url,
          alt: file.name.replace(/\.[^/.]+$/, ''),
          order: heroSlides.length
        };
        await adminApi.addHeroSlide(slideData);
        toast.success(`${file.name} added to slideshow`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    if (slideFileInputRef.current) {
      slideFileInputRef.current.value = '';
    }
    fetchData();
  };
  
  const handleDeleteSlide = async (slideId) => {
    if (!window.confirm('Remove this slide from the homepage?')) return;
    
    try {
      await adminApi.deleteHeroSlide(slideId);
      toast.success('Slide removed');
      fetchData();
    } catch (error) {
      console.error('Error deleting slide:', error);
      toast.error('Failed to delete slide');
    }
  };
  
  // Project handlers
  const handleProjectFormChange = (field, value) => {
    setProjectForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Validate all files
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    setUploading(true);
    setUploadQueue(validFiles.map(f => ({ name: f.name, status: 'pending' })));
    
    const uploadedImages = [];
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadQueue(prev => prev.map((item, idx) => 
        idx === i ? { ...item, status: 'uploading' } : item
      ));
      
      try {
        const response = await adminApi.uploadImage(file);
        uploadedImages.push({
          url: response.data.url,
          caption: file.name.replace(/\.[^/.]+$/, '') // Use filename as default caption
        });
        setUploadQueue(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'done' } : item
        ));
      } catch (error) {
        console.error('Upload error:', error);
        setUploadQueue(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'error' } : item
        ));
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    // Add all uploaded images to the project
    if (uploadedImages.length > 0) {
      setProjectForm(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      toast.success(`${uploadedImages.length} image(s) uploaded successfully!`);
    }
    
    setUploading(false);
    setUploadQueue([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const addImage = () => {
    if (!imageInput.url) {
      toast.error('Please upload an image or enter a URL');
      return;
    }
    setProjectForm(prev => ({
      ...prev,
      images: [...prev.images, { ...imageInput }]
    }));
    setImageInput({ url: '', caption: '' });
  };
  
  const removeImage = (index) => {
    setProjectForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  const removeVideo = (index) => {
    setProjectForm(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };
  
  const setAsThumbnail = (index) => {
    setProjectForm(prev => {
      const images = [...prev.images];
      const [selected] = images.splice(index, 1);
      images.push(selected); // Move to end (thumbnail position)
      return { ...prev, images };
    });
    toast.success('Thumbnail updated');
  };
  
  const moveImage = (index, direction) => {
    setProjectForm(prev => {
      const images = [...prev.images];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= images.length) return prev;
      [images[index], images[newIndex]] = [images[newIndex], images[index]];
      return { ...prev, images };
    });
  };

  const moveImageToTop = (index) => {
    setProjectForm(prev => {
      if (index === 0) return prev;
      const images = [...prev.images];
      const [item] = images.splice(index, 1);
      images.unshift(item);
      return { ...prev, images };
    });
    toast.success('Moved to first position');
  };

  // Drag & drop reordering for project images
  const handleImageDragStart = (index) => (e) => {
    setDraggedImageIndex(index);
    try { e.dataTransfer.effectAllowed = 'move'; } catch (_) {}
  };

  const handleImageDragOver = (index) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleImageDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleImageDrop = (index) => (e) => {
    e.preventDefault();
    const from = draggedImageIndex;
    setDraggedImageIndex(null);
    setDragOverIndex(null);
    if (from === null || from === index) return;
    setProjectForm(prev => {
      const images = [...prev.images];
      const [item] = images.splice(from, 1);
      images.splice(index, 0, item);
      return { ...prev, images };
    });
  };

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };

  // Drag & drop reordering for projects table (admin)
  const handleProjectDragStart = (index) => (e) => {
    setDraggedProjectIndex(index);
    try { e.dataTransfer.effectAllowed = 'move'; } catch (_) {}
  };

  const handleProjectDragOver = (index) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
    if (dragOverProjectIndex !== index) setDragOverProjectIndex(index);
  };

  const handleProjectDragLeave = () => {
    setDragOverProjectIndex(null);
  };

  const handleProjectDrop = (index) => async (e) => {
    e.preventDefault();
    const from = draggedProjectIndex;
    setDraggedProjectIndex(null);
    setDragOverProjectIndex(null);
    if (from === null || from === index) return;
    const reordered = [...projects];
    const [item] = reordered.splice(from, 1);
    reordered.splice(index, 0, item);
    setProjects(reordered);
    setSavingProjectOrder(true);
    try {
      await adminApi.reorderProjects(reordered.map((p) => p.id));
      toast.success('Project order saved');
    } catch (err) {
      console.error('reorder failed:', err);
      toast.error('Could not save new order');
      fetchData();
    } finally {
      setSavingProjectOrder(false);
    }
  };

  const handleProjectDragEnd = () => {
    setDraggedProjectIndex(null);
    setDragOverProjectIndex(null);
  };
  
  // Video upload handler
  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg'];
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Use MP4, MOV, AVI, or WebM`);
        return false;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 100MB)`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    setUploadingVideo(true);
    
    for (const file of validFiles) {
      try {
        toast.info(`Uploading ${file.name}... This may take a moment.`);
        const response = await adminApi.uploadVideo(file);
        const videoData = {
          url: response.data.url,
          caption: file.name.replace(/\.[^/.]+$/, ''),
          thumbnail: response.data.thumbnail || ''
        };
        setProjectForm(prev => ({
          ...prev,
          videos: [...prev.videos, videoData]
        }));
        toast.success(`${file.name} uploaded successfully!`);
      } catch (error) {
        console.error('Video upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadingVideo(false);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };
  
  // Parse video URL to get embed URL and type
  const parseVideoUrl = (url) => {
    // YouTube (watch, embed, shorts, live, youtu.be)
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
        thumbnail: `https://img.youtube.com/vi/${youtubeMatch[1]}/hqdefault.jpg`,
        videoId: youtubeMatch[1]
      };
    }
    
    // Vimeo (regular, player, channel slug variants)
    const vimeoMatch = url.match(/(?:vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|manage\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)/);
    if (vimeoMatch) {
      return {
        type: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
        thumbnail: '',
        videoId: vimeoMatch[1]
      };
    }
    
    // Direct video URL
    if (url.match(/\.(mp4|webm|mov|avi)(\?|$)/i)) {
      return {
        type: 'direct',
        embedUrl: url,
        thumbnail: '',
        videoId: null
      };
    }
    
    // Default - treat as direct URL
    return {
      type: 'direct',
      embedUrl: url,
      thumbnail: '',
      videoId: null
    };
  };
  
  const addVideoFromUrl = () => {
    if (!videoUrlInput.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    
    const parsed = parseVideoUrl(videoUrlInput.trim());
    
    const videoData = {
      url: parsed.embedUrl,
      caption: videoCaptionInput || 'Video',
      thumbnail: parsed.thumbnail,
      type: parsed.type
    };
    
    setProjectForm(prev => ({
      ...prev,
      videos: [...prev.videos, videoData]
    }));
    
    setVideoUrlInput('');
    setVideoCaptionInput('');
    toast.success('Video added!');
  };
  
  const openEditDialog = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      location: project.location,
      description: project.description,
      project_type: project.project_type,
      status: project.status,
      featured: project.featured,
      images: project.images || [],
      videos: project.videos || []
    });
    setVideoUrlInput('');
    setVideoCaptionInput('');
    setProjectDialogOpen(true);
  };
  
  const openNewDialog = () => {
    setEditingProject(null);
    setProjectForm(initialProjectForm);
    setVideoUrlInput('');
    setVideoCaptionInput('');
    setProjectDialogOpen(true);
  };
  
  const handleSaveProject = async () => {
    if (!projectForm.name || !projectForm.location || !projectForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      if (editingProject) {
        await adminApi.updateProject(editingProject.id, projectForm);
        toast.success('Project updated successfully');
      } else {
        await adminApi.createProject(projectForm);
        toast.success('Project created successfully');
      }
      setProjectDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    }
  };
  
  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await adminApi.deleteProject(id);
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };
  
  // Contact handlers
  const handleMarkRead = async (id) => {
    try {
      await adminApi.markContactRead(id);
      fetchData();
    } catch (error) {
      console.error('Error marking contact read:', error);
    }
  };
  
  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await adminApi.deleteContact(id);
      toast.success('Contact deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };
  
  const unreadContacts = contacts.filter(c => !c.read).length;
  
  // Dashboard view
  if (activeTab === 'dashboard') {
    return (
      <div data-testid="admin-dashboard">
        <h1 className="text-2xl font-bold mb-8">Dashboard</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-border p-6 rounded-lg"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Total Projects</p>
                <p className="text-3xl font-bold">{projects.length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-border p-6 rounded-lg"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Contact Submissions</p>
                <p className="text-3xl font-bold">{contacts.length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-border p-6 rounded-lg"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Unread Messages</p>
                <p className="text-3xl font-bold">{unreadContacts}</p>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Recent Contacts */}
        <div className="bg-white border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Recent Contact Submissions</h2>
          {contacts.length === 0 ? (
            <p className="text-muted-foreground text-sm">No contact submissions yet.</p>
          ) : (
            <div className="space-y-4">
              {contacts.slice(0, 5).map((contact) => (
                <div 
                  key={contact.id} 
                  className={`p-4 rounded-lg border ${contact.read ? 'bg-white' : 'bg-amber-50 border-amber-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{contact.name}</p>
                      <p className="text-muted-foreground text-sm">{contact.email}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm mt-2 line-clamp-2">{contact.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Hero Slides view
  if (activeTab === 'slides') {
    return (
      <div data-testid="admin-slides">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Homepage Slideshow</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Upload photos that will rotate on the homepage banner
            </p>
          </div>
        </div>
        
        {/* Upload Area */}
        <div className="bg-white border border-border rounded-lg p-6 mb-8">
          <input
            ref={slideFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleSlideUpload}
            className="hidden"
            id="slide-upload"
            multiple
          />
          <Button
            onClick={() => slideFileInputRef.current?.click()}
            disabled={uploading}
            className="w-full py-12 border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/50 text-foreground"
            variant="ghost"
            data-testid="upload-slide-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 mr-3" />
                Click to Upload Photos for Slideshow
              </>
            )}
          </Button>
          <p className="text-center text-muted-foreground text-sm mt-3">
            Select multiple images at once • JPEG, PNG, WebP, GIF • Max 10MB each
          </p>
        </div>
        
        {/* Current Slides */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-40 rounded-lg" />)}
          </div>
        ) : heroSlides.length === 0 ? (
          <div className="text-center py-16 bg-white border border-border rounded-lg">
            <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No slides yet. Upload photos above!</p>
            <p className="text-muted-foreground text-sm mt-1">Default images will show until you add your own.</p>
          </div>
        ) : (
          <div>
            <h2 className="text-lg font-semibold mb-4">Current Slides ({heroSlides.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {heroSlides.map((slide, index) => (
                <motion.div
                  key={slide.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative group bg-white border border-border rounded-lg overflow-hidden"
                  data-testid={`slide-${index}`}
                >
                  <div className="aspect-video">
                    <img 
                      src={slide.url} 
                      alt={slide.alt || `Slide ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDeleteSlide(slide.id)}
                      data-testid={`delete-slide-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                  <div className="p-2 border-t border-border">
                    <p className="text-xs text-muted-foreground truncate">
                      {slide.alt || `Slide ${index + 1}`}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // About Images view
  if (activeTab === 'about') {
    return (
      <div data-testid="admin-about">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">About Page Images</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Upload photos for the About page
            </p>
          </div>
        </div>
        
        {/* Upload Area */}
        <div className="bg-white border border-border rounded-lg p-6 mb-8">
          <input
            ref={aboutFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => handleAboutImageUpload(e, 'main')}
            className="hidden"
            id="about-upload"
            multiple
          />
          <Button
            onClick={() => aboutFileInputRef.current?.click()}
            disabled={uploading}
            className="w-full py-12 border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/50 text-foreground"
            variant="ghost"
            data-testid="upload-about-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 mr-3" />
                Click to Upload Photos for About Page
              </>
            )}
          </Button>
          <p className="text-center text-muted-foreground text-sm mt-3">
            Select multiple images at once • JPEG, PNG, WebP, GIF • Max 10MB each
          </p>
        </div>

        {/* Director Photo Upload */}
        <div className="bg-white border border-border rounded-lg p-6 mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold">Director Photo</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Used in the "Meet the Director" section on the About page. Uploading a new photo will replace the existing one.
              </p>
            </div>
            {aboutImages.find((img) => img.section === 'director') && (
              <img
                src={aboutImages.find((img) => img.section === 'director').url}
                alt="Director"
                className="w-20 h-24 object-cover rounded-md border border-border"
                data-testid="current-director-photo"
              />
            )}
          </div>
          <input
            ref={directorFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => handleAboutImageUpload(e, 'director')}
            className="hidden"
            id="director-upload"
          />
          <Button
            onClick={() => directorFileInputRef.current?.click()}
            disabled={uploading}
            className="w-full py-8 border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/50 text-foreground"
            variant="ghost"
            data-testid="upload-director-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-3" />
                Upload Director Photo
              </>
            )}
          </Button>
        </div>
        
        {/* Current Images */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-40 rounded-lg" />)}
          </div>
        ) : aboutImages.length === 0 ? (
          <div className="text-center py-16 bg-white border border-border rounded-lg">
            <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No images yet. Upload photos above!</p>
            <p className="text-muted-foreground text-sm mt-1">Default images will show until you add your own.</p>
          </div>
        ) : (
          <div>
            <h2 className="text-lg font-semibold mb-4">Current Images ({aboutImages.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {aboutImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative group bg-white border border-border rounded-lg overflow-hidden"
                  data-testid={`about-image-${index}`}
                >
                  <div className="aspect-video">
                    <img 
                      src={image.url} 
                      alt={image.alt || `Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDeleteAboutImage(image.id)}
                      data-testid={`delete-about-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                  <div className="p-2 border-t border-border flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground truncate">
                      {image.alt || `Image ${index + 1}`}
                    </p>
                    {image.section && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-foreground/70 shrink-0">
                        {image.section}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Projects view
  if (activeTab === 'content') {
    const sections = [
      {
        title: 'Home — Hero',
        fields: [
          { key: 'home.hero.eyebrow', label: 'Small eyebrow text', type: 'text' },
          { key: 'home.hero.heading_line1', label: 'Headline — Line 1', type: 'text' },
          { key: 'home.hero.heading_line2', label: 'Headline — Line 2', type: 'text' },
          { key: 'home.hero.subtext', label: 'Hero tagline (below headline)', type: 'text' },
          { key: 'home.hero.cta_primary', label: 'Primary button text', type: 'text' },
          { key: 'home.hero.cta_secondary', label: 'Secondary button text', type: 'text' },
        ],
      },
      {
        title: 'Home — Who We Are',
        fields: [
          { key: 'home.who.eyebrow', label: 'Eyebrow', type: 'text' },
          { key: 'home.who.heading', label: 'Heading', type: 'text' },
          { key: 'home.who.p1', label: 'First paragraph', type: 'textarea' },
          { key: 'home.who.p2', label: 'Second paragraph', type: 'textarea' },
        ],
      },
      {
        title: 'Home — Our Services',
        fields: [
          { key: 'home.services.eyebrow', label: 'Section eyebrow', type: 'text' },
          { key: 'home.services.heading', label: 'Section heading', type: 'text' },
          { key: 'home.services.subdivisions.title', label: 'Service 1 title (Subdivisions)', type: 'text' },
          { key: 'home.services.subdivisions.desc', label: 'Service 1 description', type: 'textarea' },
          { key: 'home.services.residential.title', label: 'Service 2 title (Residential)', type: 'text' },
          { key: 'home.services.residential.desc', label: 'Service 2 description', type: 'textarea' },
          { key: 'home.services.social_housing.title', label: 'Service 3 title (Social Housing)', type: 'text' },
          { key: 'home.services.social_housing.desc', label: 'Service 3 description', type: 'textarea' },
        ],
      },
      {
        title: 'Home — Community Impact',
        fields: [
          { key: 'home.community.eyebrow', label: 'Eyebrow', type: 'text' },
          { key: 'home.community.heading', label: 'Heading', type: 'text' },
          { key: 'home.community.p1', label: 'First paragraph', type: 'textarea' },
          { key: 'home.community.p2', label: 'Second paragraph', type: 'textarea' },
        ],
      },
      {
        title: 'Home — Final Call to Action',
        fields: [
          { key: 'home.cta.heading', label: 'Heading', type: 'text' },
          { key: 'home.cta.subtext', label: 'Subtext', type: 'textarea' },
        ],
      },
      {
        title: 'About — Hero',
        fields: [
          { key: 'about.hero.eyebrow', label: 'Eyebrow', type: 'text' },
          { key: 'about.hero.heading', label: 'Headline', type: 'text' },
          { key: 'about.hero.subtext', label: 'Subtext', type: 'textarea' },
        ],
      },
      {
        title: 'About — Vision & Mission',
        fields: [
          { key: 'about.overview.vision_title', label: 'Vision title', type: 'text' },
          { key: 'about.overview.vision_body', label: 'Vision body', type: 'textarea' },
          { key: 'about.overview.mission_title', label: 'Mission title', type: 'text' },
          { key: 'about.overview.mission_body', label: 'Mission body', type: 'textarea' },
        ],
      },
      {
        title: 'About — Meet the Director',
        fields: [
          { key: 'about.director.eyebrow', label: 'Eyebrow', type: 'text' },
          { key: 'about.director.heading', label: 'Section heading', type: 'text' },
          { key: 'about.director.name', label: 'Director name', type: 'text' },
          { key: 'about.director.title', label: 'Director title', type: 'text' },
          { key: 'about.director.affiliation', label: 'Company / location line', type: 'text' },
          { key: 'about.director.p1', label: 'Bio paragraph 1', type: 'textarea' },
          { key: 'about.director.p2', label: 'Bio paragraph 2', type: 'textarea' },
          { key: 'about.director.p3', label: 'Bio paragraph 3', type: 'textarea' },
          { key: 'about.director.p4', label: 'Bio paragraph 4', type: 'textarea' },
          { key: 'about.director.p5', label: 'Bio paragraph 5', type: 'textarea' },
        ],
      },
      {
        title: 'About — Community First',
        fields: [
          { key: 'about.community.eyebrow', label: 'Eyebrow', type: 'text' },
          { key: 'about.community.heading', label: 'Heading', type: 'text' },
          { key: 'about.community.p1', label: 'First paragraph', type: 'textarea' },
          { key: 'about.community.p2', label: 'Second paragraph', type: 'textarea' },
        ],
      },
      {
        title: 'Contact — Hero',
        fields: [
          { key: 'contact.hero.heading', label: 'Headline', type: 'text' },
          { key: 'contact.hero.subtext', label: 'Subtext', type: 'textarea' },
        ],
      },
      {
        title: 'Contact — Information',
        fields: [
          { key: 'contact.info.address_line1', label: 'Address — line 1', type: 'text' },
          { key: 'contact.info.address_line2', label: 'Address — line 2', type: 'text' },
          { key: 'contact.info.email', label: 'Email', type: 'text' },
          { key: 'contact.info.phone', label: 'Phone (leave empty to hide)', type: 'text' },
          { key: 'contact.info.hours', label: 'Business hours (newlines OK)', type: 'textarea' },
        ],
      },
    ];

    const setField = (key, value) => {
      setSiteContentDraft((prev) => ({ ...prev, [key]: value }));
    };

    const isDirty = sections.some((section) =>
      section.fields.some((f) => (siteContentDraft[f.key] ?? '') !== (siteContent[f.key] ?? ''))
    );

    const handleSaveContent = async () => {
      setSavingContent(true);
      try {
        const updates = {};
        sections.forEach((section) => {
          section.fields.forEach((f) => {
            if ((siteContentDraft[f.key] ?? '') !== (siteContent[f.key] ?? '')) {
              updates[f.key] = siteContentDraft[f.key] ?? '';
            }
          });
        });
        if (Object.keys(updates).length === 0) {
          toast.info('No changes to save');
          return;
        }
        const res = await adminApi.updateSiteContent(updates);
        setSiteContent(res.data || {});
        setSiteContentDraft(res.data || {});
        refreshSiteContent();
        toast.success('Site content saved. Refresh the public pages to see changes.');
      } catch (err) {
        console.error('Save content error:', err);
        toast.error('Could not save content');
      } finally {
        setSavingContent(false);
      }
    };

    const handleResetDraft = () => {
      setSiteContentDraft(siteContent);
      toast.info('Reverted unsaved changes');
    };

    return (
      <div data-testid="admin-site-content">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Site Content</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Edit any text on the Home, About, and Contact pages. Changes save instantly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleResetDraft}
              disabled={!isDirty || savingContent}
              data-testid="revert-content-btn"
            >
              Revert
            </Button>
            <Button
              onClick={handleSaveContent}
              disabled={!isDirty || savingContent}
              data-testid="save-content-btn"
            >
              {savingContent ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {sections.map((section) => (
            <div
              key={section.title}
              className="bg-white border border-border rounded-lg p-6"
            >
              <h2 className="text-base font-semibold mb-5 pb-3 border-b border-border">
                {section.title}
              </h2>
              <div className="space-y-5">
                {section.fields.map((field) => (
                  <div key={field.key}>
                    <Label className="text-xs text-muted-foreground font-medium mb-2 block">
                      {field.label}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={siteContentDraft[field.key] ?? ''}
                        onChange={(e) => setField(field.key, e.target.value)}
                        rows={3}
                        className="text-sm"
                        data-testid={`content-field-${field.key}`}
                      />
                    ) : (
                      <Input
                        value={siteContentDraft[field.key] ?? ''}
                        onChange={(e) => setField(field.key, e.target.value)}
                        className="text-sm"
                        data-testid={`content-field-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {isDirty && (
          <div className="fixed bottom-6 right-6 bg-primary text-primary-foreground px-5 py-3 rounded-full shadow-lg flex items-center gap-3 text-sm">
            <span>Unsaved changes</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSaveContent}
              disabled={savingContent}
              className="h-7"
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Projects view
  if (activeTab === 'projects') {
    return (
      <div data-testid="admin-projects">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">Projects</h1>
          <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="rounded-full" data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProject ? 'Edit Project' : 'Add New Project'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project Name *</Label>
                    <Input
                      value={projectForm.name}
                      onChange={(e) => handleProjectFormChange('name', e.target.value)}
                      placeholder="Riverside Heights"
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Input
                      value={projectForm.location}
                      onChange={(e) => handleProjectFormChange('location', e.target.value)}
                      placeholder="Palmerston North, Manawatu"
                      data-testid="project-location-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={projectForm.description}
                    onChange={(e) => handleProjectFormChange('description', e.target.value)}
                    placeholder="Describe the project..."
                    rows={4}
                    data-testid="project-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project Type</Label>
                    <Select
                      value={projectForm.project_type}
                      onValueChange={(value) => handleProjectFormChange('project_type', value)}
                    >
                      <SelectTrigger data-testid="project-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="social_housing">Social Housing</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="subdivision">Subdivision</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={projectForm.status}
                      onValueChange={(value) => handleProjectFormChange('status', value)}
                    >
                      <SelectTrigger data-testid="project-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="ongoing">Ongoing</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Switch
                    checked={projectForm.featured}
                    onCheckedChange={(checked) => handleProjectFormChange('featured', checked)}
                    data-testid="project-featured-switch"
                  />
                  <Label>Featured Project</Label>
                </div>
                
                {/* Images Section */}
                <div className="space-y-4">
                  <Label>Project Images</Label>
                  
                  <div className="border border-dashed border-border rounded-lg p-4 space-y-4">
                    {/* Upload Button - Multiple Files */}
                    <div className="flex flex-col gap-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="image-upload"
                        multiple
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full py-8 border-2 border-dashed"
                        data-testid="upload-image-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Uploading {uploadQueue.filter(q => q.status === 'done').length}/{uploadQueue.length}...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 mr-2" />
                            Click to Upload Photos (Select Multiple)
                          </>
                        )}
                      </Button>
                      
                      {/* Upload Progress */}
                      {uploadQueue.length > 0 && (
                        <div className="space-y-1">
                          {uploadQueue.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              {item.status === 'pending' && <div className="w-3 h-3 rounded-full bg-gray-300" />}
                              {item.status === 'uploading' && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                              {item.status === 'done' && <CheckCircle className="w-3 h-3 text-green-500" />}
                              {item.status === 'error' && <X className="w-3 h-3 text-red-500" />}
                              <span className="truncate">{item.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Manual URL Input (collapsible) */}
                    <details className="text-sm">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        Or add image by URL
                      </summary>
                      <div className="mt-3 grid grid-cols-1 gap-3">
                        <Input
                          value={imageInput.url}
                          onChange={(e) => setImageInput(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="Paste image URL here"
                          data-testid="image-url-input"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={imageInput.caption}
                            onChange={(e) => setImageInput(prev => ({ ...prev, caption: e.target.value }))}
                            placeholder="Caption (optional)"
                          />
                          <Button type="button" onClick={addImage} data-testid="add-image-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Add URL
                          </Button>
                        </div>
                      </div>
                    </details>
                    
                    {/* Added Images List */}
                    {projectForm.images.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-border">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground font-medium">
                            Added Images ({projectForm.images.length}) — Drag to reorder
                          </p>
                          <p className="text-xs text-amber-600">★ Last image = Thumbnail</p>
                        </div>
                        <div className="space-y-2">
                          {projectForm.images.map((img, index) => {
                            const isThumbnail = index === projectForm.images.length - 1;
                            const isDragging = draggedImageIndex === index;
                            const isDragOver = dragOverIndex === index && draggedImageIndex !== index;
                            return (
                              <div 
                                key={index}
                                draggable
                                onDragStart={handleImageDragStart(index)}
                                onDragOver={handleImageDragOver(index)}
                                onDragLeave={handleImageDragLeave}
                                onDrop={handleImageDrop(index)}
                                onDragEnd={handleImageDragEnd}
                                className={`flex items-center gap-3 p-2 rounded-lg border transition-all select-none ${
                                  isDragging ? 'opacity-40' : ''
                                } ${
                                  isDragOver ? 'border-primary ring-2 ring-primary/30 bg-primary/5' :
                                  isThumbnail ? 'border-amber-400 bg-amber-50' : 'border-border bg-white'
                                }`}
                                data-testid={`project-image-row-${index}`}
                              >
                                <GripVertical
                                  className="w-4 h-4 text-muted-foreground cursor-grab shrink-0"
                                  title="Drag to reorder"
                                />
                                <span className="text-xs text-muted-foreground w-6 text-center font-mono shrink-0">
                                  {index + 1}
                                </span>
                                <img src={img.url} alt={img.caption} className="w-20 h-14 object-cover rounded shrink-0 pointer-events-none" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm truncate">{img.caption || 'No caption'}</p>
                                  {isThumbnail && (
                                    <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                                      <Star className="w-3 h-3 fill-amber-400" /> Thumbnail
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => moveImageToTop(index)}
                                    disabled={index === 0}
                                    title="Move to first position"
                                    data-testid={`move-top-${index}`}
                                  >
                                    <ArrowUpToLine className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => moveImage(index, 'up')}
                                    disabled={index === 0}
                                    title="Move up"
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => moveImage(index, 'down')}
                                    disabled={index === projectForm.images.length - 1}
                                    title="Move down"
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </Button>
                                  {!isThumbnail && (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className="text-xs"
                                      onClick={() => setAsThumbnail(index)}
                                      title="Set as project thumbnail"
                                    >
                                      <Star className="w-3 h-3 mr-1" />
                                      Set Thumbnail
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    onClick={() => removeImage(index)}
                                    title="Remove image"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Videos Section */}
                <div className="space-y-4">
                  <Label>Project Videos</Label>
                  
                  <div className="border border-dashed border-border rounded-lg p-4 space-y-4">
                    {/* Video Upload Button */}
                    <div className="flex flex-col gap-3">
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/mpeg"
                        onChange={handleVideoUpload}
                        className="hidden"
                        id="video-upload"
                        multiple
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => videoInputRef.current?.click()}
                        disabled={uploadingVideo}
                        className="w-full py-6 border-2 border-dashed"
                        data-testid="upload-video-btn"
                      >
                        {uploadingVideo ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Uploading Video...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 mr-2" />
                            Upload Video File
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        MP4, MOV, AVI, WebM • Max 100MB each
                      </p>
                    </div>
                    
                    {/* Video URL Input */}
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm font-medium mb-3">Or add video from URL</p>
                      <div className="space-y-3">
                        <Input
                          placeholder="Paste YouTube, Vimeo, or video URL"
                          value={videoUrlInput}
                          onChange={(e) => setVideoUrlInput(e.target.value)}
                          data-testid="video-url-input"
                        />
                        <div className="flex gap-2">
                          <Input
                            placeholder="Video title (optional)"
                            value={videoCaptionInput}
                            onChange={(e) => setVideoCaptionInput(e.target.value)}
                            className="flex-1"
                          />
                          <Button type="button" onClick={addVideoFromUrl}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Video
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Supports: YouTube, Vimeo, or direct video links
                        </p>
                      </div>
                    </div>
                    
                    {/* Added Videos List */}
                    {projectForm.videos && projectForm.videos.length > 0 && (
                      <div className="space-y-2 pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground font-medium">Added Videos ({projectForm.videos.length})</p>
                        <div className="space-y-2">
                          {projectForm.videos.map((video, index) => (
                            <div 
                              key={index} 
                              className="flex items-center gap-3 p-2 rounded-lg border border-border bg-white"
                            >
                              <div className="relative w-24 h-16 bg-secondary rounded overflow-hidden flex items-center justify-center">
                                {video.thumbnail ? (
                                  <img src={video.thumbnail} alt={video.caption} className="w-full h-full object-cover" />
                                ) : (
                                  <Video className="w-8 h-8 text-muted-foreground" />
                                )}
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                  <Play className="w-6 h-6 text-white fill-white" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm truncate">{video.caption || 'Video'}</p>
                                  {video.type && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      video.type === 'youtube' ? 'bg-red-100 text-red-700' :
                                      video.type === 'vimeo' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {video.type === 'youtube' ? 'YouTube' : 
                                       video.type === 'vimeo' ? 'Vimeo' : 'Video'}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">{video.url}</p>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => removeVideo(index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <DialogClose asChild>
                    <Button variant="outline" className="rounded-full">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleSaveProject} className="rounded-full" data-testid="save-project-btn">
                    {editingProject ? 'Update Project' : 'Create Project'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-lg" />)}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20 bg-white border border-border rounded-lg">
            <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No projects yet. Add your first project!</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-border bg-secondary/30 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Drag rows by the grip handle to reorder how projects appear on the public Projects page.
              </p>
              {savingProjectOrder && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                </span>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project, index) => {
                  const isDragging = draggedProjectIndex === index;
                  const isDragOver = dragOverProjectIndex === index && draggedProjectIndex !== index;
                  return (
                    <TableRow
                      key={project.id}
                      draggable
                      onDragStart={handleProjectDragStart(index)}
                      onDragOver={handleProjectDragOver(index)}
                      onDragLeave={handleProjectDragLeave}
                      onDrop={handleProjectDrop(index)}
                      onDragEnd={handleProjectDragEnd}
                      className={`transition-colors ${
                        isDragging ? 'opacity-40' : ''
                      } ${
                        isDragOver ? 'bg-primary/5 ring-2 ring-primary/30' : ''
                      }`}
                      data-testid={`project-row-${project.id}`}
                    >
                      <TableCell className="cursor-grab w-10">
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{project.name}</p>
                          <p className="text-muted-foreground text-sm">{project.location}</p>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{(project.project_type || '').replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs rounded-full ${statusColors[project.status]}`}>
                          {project.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {project.featured && <CheckCircle className="w-4 h-4 text-green-600" />}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(project)}
                            data-testid={`edit-project-${project.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteProject(project.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    );
  }
  
  // Contacts view
  return (
    <div data-testid="admin-contacts">
      <h1 className="text-2xl font-bold mb-8">Contact Submissions</h1>
      
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-32 rounded-lg" />)}
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-20 bg-white border border-border rounded-lg">
          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No contact submissions yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white border rounded-lg p-6 ${
                contact.read ? 'border-border' : 'border-amber-300 bg-amber-50/50'
              }`}
              data-testid={`contact-row-${contact.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    contact.read ? 'bg-secondary' : 'bg-amber-100'
                  }`}>
                    <Mail className={`w-5 h-5 ${contact.read ? 'text-muted-foreground' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{contact.name}</p>
                      {!contact.read && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">New</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {contact.email}
                      </span>
                      {contact.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {contact.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(contact.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {!contact.read && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleMarkRead(contact.id)}
                      data-testid={`mark-read-${contact.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Mark Read
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDeleteContact(contact.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-contact-${contact.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-secondary/50 p-4 rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{contact.message}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
